import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';

interface TopBarContentContextType {
  topBarContent: ReactNode;
  setTopBarContent: (content: ReactNode) => void;
  clearTopBarContent: () => void;
}

const TopBarContentContext = createContext<TopBarContentContextType>({
  topBarContent: null,
  setTopBarContent: () => {},
  clearTopBarContent: () => {},
});

export function TopBarContentProvider({ children }: { children: ReactNode }) {
  const [topBarContent, setTopBarContentState] = useState<ReactNode>(null);

  const setTopBarContent = useCallback((content: ReactNode) => {
    setTopBarContentState(content);
  }, []);

  const clearTopBarContent = useCallback(() => {
    setTopBarContentState(null);
  }, []);

  return (
    <TopBarContentContext.Provider value={{ topBarContent, setTopBarContent, clearTopBarContent }}>
      {children}
    </TopBarContentContext.Provider>
  );
}

export function useTopBarContent(): TopBarContentContextType {
  return useContext(TopBarContentContext);
}

/**
 * Hook for pages to set custom top bar content (e.g. breadcrumbs).
 * Automatically clears on unmount.
 * Pass a stable ReactNode (useMemo or null) to avoid unnecessary re-renders.
 */
export function useSetTopBarContent(content: ReactNode) {
  const { setTopBarContent, clearTopBarContent } = useTopBarContent();
  useEffect(() => {
    setTopBarContent(content);
    return () => clearTopBarContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}



